import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Room } from '../../../core/models/entities/room';
import { BookingsScope } from '../../../core/store/bookings/bookings.state';
import { CalendarRoomsService } from '../../../core/services/calendar-rooms.service';

@Component({
  selector: 'app-bookings-rooms-sidebar',
  template: `
    @if (showTitle()) {
      <div class="mb-3 text-base font-semibold text-gray-800">
        {{ 'bookings.month-view.rooms-title' | transloco }}
      </div>
    }
    @if (searchable()) {
      <div class="relative mb-3">
        <i
          class="far fa-search pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-xs text-gray-400"
        ></i>
        <input
          #searchInput
          type="text"
          class="focus:border-primary-500 focus:ring-primary-500 block w-full rounded-lg border-gray-200 py-1.5 ps-8 pe-2 text-[13px]"
          [placeholder]="'bookings.rooms-filter.search-placeholder' | transloco"
          [value]="searchText()"
          (input)="searchText.set($any($event.target).value)"
        />
      </div>
    }
    <div class="space-y-2.5" #roomsList>
      @if (!searchText()) {
        <label
          class="flex cursor-pointer items-center gap-3 border-b border-gray-200 pb-2.5 text-[13px] font-medium text-gray-800"
        >
          <input
            type="checkbox"
            class="text-primary-600 checked:border-primary-600 checked:bg-primary-600 focus:ring-primary-500 h-[18px] w-[18px] shrink-0 cursor-pointer rounded border-gray-300 bg-white focus:ring-2"
            [checked]="calendarRooms.areAllShown(scope())"
            [indeterminate]="
              calendarRooms.shownRooms(scope()).length > 0 &&
              !calendarRooms.areAllShown(scope())
            "
            (change)="
              calendarRooms.setAllShown(scope(), $any($event.target).checked)
            "
          />
          {{ 'bookings.month-view.select-all-rooms' | transloco }}
        </label>
      }
      @for (room of filteredRooms(); track room.id) {
        <div class="group flex items-center gap-1">
          <label
            class="flex min-w-0 grow cursor-pointer items-center gap-3 text-[13px] text-gray-700"
          >
            <input
              type="checkbox"
              class="h-[18px] w-[18px] shrink-0 cursor-pointer rounded border-gray-300 bg-white focus:ring-2"
              [ngClass]="calendarRooms.colorOf(room).checkbox"
              [checked]="calendarRooms.isShown(scope(), room)"
              (change)="toggleRoom(room, $any($event.target).checked)"
            />
            <span
              class="truncate"
              [attr.data-room-id]="room.id"
              [tooltip]="truncatedRoomIds().has(room.id) ? room.full_name : ''"
              [showDelay]="750"
              [style.cursor]="'pointer'"
              >{{ room.full_name }}</span
            >
          </label>
          @if (showRoomMenu()) {
            <app-calendar-room-menu
              class="-my-1 hidden opacity-0 group-hover:opacity-100 focus-within:opacity-100 lg:block"
              [room]="room"
            />
          }
        </div>
      } @empty {
        <div class="text-[13px] text-gray-500">
          {{ 'bookings.rooms-filter.no-result' | transloco }}
        </div>
      }
    </div>
  `,
  standalone: false,
})
export class BookingsRoomsSidebarComponent {
  scope = input.required<BookingsScope>();
  showTitle = input(true);
  searchable = input(false);
  showRoomMenu = input(false);
  searchText = signal('');

  filteredRooms = computed(() => {
    const normalizedSearch = this.normalize(this.searchText());
    return this.calendarRooms
      .activeRooms()
      .filter((room) =>
        this.normalize(room.full_name).includes(normalizedSearch),
      );
  });

  protected calendarRooms = inject(CalendarRoomsService);
  private destroyRef = inject(DestroyRef);

  roomsList = viewChild<ElementRef<HTMLElement>>('roomsList');
  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  truncatedRoomIds = signal(new Set<number>());

  constructor() {
    afterNextRender(() => {
      this.watchTruncatedRoomNames();
      this.searchInput()?.nativeElement.focus();
    });
  }

  toggleRoom(room: Room, shown: boolean) {
    this.calendarRooms.toggle(this.scope(), room, shown);
  }

  private normalize(text: string): string {
    return (text ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private watchTruncatedRoomNames() {
    const roomsListElement = this.roomsList()?.nativeElement;
    if (!roomsListElement) {
      return;
    }
    const resizeObserver = new ResizeObserver(() =>
      this.truncatedRoomIds.set(this.findTruncatedRoomIds(roomsListElement)),
    );
    resizeObserver.observe(roomsListElement);
    this.destroyRef.onDestroy(() => resizeObserver.disconnect());
  }

  private findTruncatedRoomIds(roomsListElement: HTMLElement): Set<number> {
    const roomNameElements = Array.from(
      roomsListElement.querySelectorAll<HTMLElement>('[data-room-id]'),
    );
    return new Set(
      roomNameElements
        .filter((element) => element.scrollWidth > element.clientWidth)
        .map((element) => Number(element.dataset['roomId'])),
    );
  }
}
